import {
  Field,
  TextArea,
  TextCheck,
} from "./AdminFields";
import { DEFAULT_THEME_ICONS } from "../components/HomeBio";

const HOME_PITCH_FIELDS_BEFORE_THEMES = [
  { key: "greeting", label: "Greeting", rows: 3 },
  { key: "researchLead", label: "Research lead", rows: 4 },
  { key: "themesLead", label: "Themes lead-in", rows: 2 },
];

const HOME_PITCH_FIELDS_AFTER_THEMES = [
  { key: "supervisors", label: "Supervisors / scholarship", rows: 3 },
  { key: "grounding", label: "Grounding (community / practice)", rows: 3 },
  { key: "proof", label: "Proof line (venues / awards)", rows: 2 },
  { key: "closing", label: "Closing", rows: 3 },
  { key: "invite", label: "Invite to connect", rows: 2 },
];

const THEMES_STYLE_OPTIONS = [
  {
    id: "bullets",
    label: "Bullet list",
    hint: "Classic disc bullets with green labels",
  },
  {
    id: "numbered",
    label: "Numbered list",
    hint: "1, 2, 3 before each theme",
  },
  {
    id: "icons",
    label: "Icons + text",
    hint: "Custom emoji/icon per theme",
  },
  {
    id: "plain",
    label: "Plain stacked",
    hint: "No bullets; spaced paragraphs",
  },
];

/**
 * Shared Home bio form: pitch copy + themes formatting.
 * Used by /admin Home bio tab and Visual Studio side panel.
 */
const HomePitchEditor = ({
  pitch = {},
  pillars = [],
  onPitchChange,
  footer = null,
  note = null,
}) => {
  const setField = (key, value) => {
    onPitchChange({ ...pitch, [key]: value });
  };

  const themesStyle = pitch.themesStyle || "bullets";
  const themeIcons = {
    ...DEFAULT_THEME_ICONS,
    ...(pitch.themeIcons || {}),
  };

  return (
    <div className="space-y-5">
      {note}

      {HOME_PITCH_FIELDS_BEFORE_THEMES.map((field) => (
        <TextArea
          key={field.key}
          label={field.label}
          value={pitch[field.key] || ""}
          rows={field.rows}
          onChange={(v) => setField(field.key, v)}
        />
      ))}

      <Field label="Themes formatting">
        <div className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <p className="text-[11px] text-gray-500">
            Questions come from Research pillars. Here you only choose how they
            look on Home.
          </p>
          <div className="flex flex-wrap gap-2">
            {THEMES_STYLE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setField("themesStyle", option.id)}
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                  themesStyle === option.id
                    ? "bg-green-solid text-white"
                    : "border border-gray-300 text-gray-600 hover:border-green hover:text-green dark:hover:text-green-dark dark:border-gray-600 dark:text-gray-300"
                }`}
                title={option.hint}
              >
                {option.label}
              </button>
            ))}
          </div>
          <TextCheck
            label="Show goal labels (Reflection / Alignment / Agency)"
            checked={pitch.themesShowLabels !== false}
            onChange={(v) => setField("themesShowLabels", v)}
          />
          <TextCheck
            label="Link each theme to Research agenda"
            checked={pitch.themesLinkToResearch !== false}
            onChange={(v) => setField("themesLinkToResearch", v)}
          />

          {themesStyle === "icons" && (
            <div className="space-y-2 border-t border-gray-200 pt-3 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Icons (emoji or short text)
              </p>
              {(pillars.length
                ? pillars
                : Object.keys(DEFAULT_THEME_ICONS).map((id) => ({ id }))
              ).map((pillar) => (
                <div key={pillar.id} className="flex items-center gap-2">
                  <span className="w-24 shrink-0 text-sm text-gray-500">
                    {pillar.bigGoal || pillar.id}
                  </span>
                  <input
                    type="text"
                    className="mt-0 w-24 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-lightText dark:border-gray-600 dark:bg-gray-900 dark:text-darkText"
                    value={themeIcons[pillar.id] || ""}
                    placeholder="💭"
                    onChange={(e) =>
                      setField("themeIcons", {
                        ...themeIcons,
                        [pillar.id]: e.target.value,
                      })
                    }
                  />
                  <span className="text-lg" aria-hidden>
                    {themeIcons[pillar.id] || "•"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Field>

      {HOME_PITCH_FIELDS_AFTER_THEMES.map((field) => (
        <TextArea
          key={field.key}
          label={field.label}
          value={pitch[field.key] || ""}
          rows={field.rows}
          onChange={(v) => setField(field.key, v)}
        />
      ))}

      {footer}
    </div>
  );
};

export default HomePitchEditor;
